import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { DiamondRow } from './DiamondRow'
import { MediaModal } from './MediaModal'
import {
  chatApi, sellerApi, adminApi,
  type ChatMessageHistory, type ChatAction, type ChatConfirm, type ChatStep,
} from '../api/endpoints'
import { stoneKey } from '../lib/stoneKey'
import { useApp } from '../store'
import { watchAuthState } from '../lib/firebase'
import { ApiError, type Diamond, type SearchFilters } from '../api/types'

/**
 * Gemma — the always-present floating AI assistant for DiaEx.
 *
 * A compact, on-brand chat surface that lives on every buyer-facing page:
 * a resting FAB (bottom-right) that expands into a slide-up sheet (mobile =
 * full-height bottom sheet, desktop = ~400px corner panel). It runs the
 * SAME role-scoped AI as /chat via `chatApi.send` — buyer concierge,
 * supplier stock manager, admin ops — and reuses the exact inline rendering
 * primitives (DiamondRow result cards, MediaModal for KYC-gated supplier
 * contact, price blur for anon, supplier-intent confirm cards).
 *
 * Gemma COMPLEMENTS the full /chat page + Cmd-K launcher; it does not
 * replace them. It is hidden on /chat (redundant) and /admin/* (the console
 * has its own /chat access). All gating — anon price-blur, KYC-gated
 * contact, role personas, refStockId masking, sold/demo exclusion — is
 * enforced server-side; Gemma just renders what the API returns.
 *
 * Open/closed state + the conversation persist within a browser session
 * (sessionStorage), so navigating between pages keeps Gemma's thread.
 */

type Role = 'admin' | 'supplier' | 'buyer' | null

interface GemmaMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  text: string
  thinking?: boolean
  /** Light agentic trail — what Gemma searched/checked (server-built). */
  steps?: ChatStep[]
  results?: Diamond[]
  /** Filters the search ran — echoed as lastFilters so follow-ups refine. */
  appliedFilters?: SearchFilters
  actions?: ChatAction[]
  confirm?: ChatConfirm
  confirmResolved?: 'done' | 'skipped'
  ts: number
}

// Session-scoped persistence — the thread survives page navigation but not a
// new tab / a fresh session, matching a "concierge for this visit" feel.
const OPEN_KEY = 'diaex:gemma:open'
const MSGS_KEY = 'diaex:gemma:messages'

// Routes where the floating assistant is hidden. /chat is the full-page
// version of the same AI (redundant); /admin/* keeps its console uncluttered
// (admins still have the /chat console + Cmd-K). The auth/signup flow hides
// it too — the FAB sat exactly on the wizard's bottom-right nav arrows.
function isHiddenPath(pathname: string): boolean {
  return pathname === '/chat' || pathname.startsWith('/admin')
    || pathname === '/signup' || pathname === '/register' || pathname === '/signin'
}

// ── Role-scoped greeting + suggestion chips ─────────────────────────────
function greetingFor(role: Role): string {
  if (role === 'supplier') {
    return "Hi, I'm Gemma. I can help you manage your stock — mark a stone unavailable, update a price, or pull up your inventory. What do you need?"
  }
  if (role === 'admin') {
    return "Hi, I'm Gemma. Ask me for a platform snapshot, jump to KYC, orders, or the fees ledger, or dig into anything on the console."
  }
  return "Hi, I'm Gemma — tell me what you're looking for, or ask me anything about diamonds."
}

function suggestionsFor(role: Role): string[] {
  if (role === 'supplier') {
    return ['Is my stock feed connected?', 'Mark a stone unavailable', 'Update my contact info']
  }
  if (role === 'admin') {
    return ['Platform snapshot', 'Open enquiries', "Today's orders"]
  }
  return [
    'Round 1-2ct under $5k',
    "What's the difference between VS1 and VVS2?",
    'Biggest stone you have',
  ]
}

// ── Live admin context (buyer order status is a server-side tool now) ────
const ADMIN_METRIC_RE = /\b(how many|how much|count|number of|stats|statistics|metrics|kpi|snapshot|overview|total|volume|dashboard)\b/i

async function buildAdminContext(userText: string): Promise<string | undefined> {
  if (!ADMIN_METRIC_RE.test(userText)) return undefined
  try {
    const s = await adminApi.summary()
    return `=== LIVE PLATFORM METRICS (quote these figures; do not invent others) ===\n`
      + `- Stones (total catalog): ${s.stones.toLocaleString()}\n`
      + `- Users (all roles): ${s.users.toLocaleString()}\n`
      + `- Enquiries (buyer→supplier, total): ${s.enquiries.toLocaleString()}\n`
      + `=== END ===`
  } catch {
    return undefined
  }
}

function loadMessages(): GemmaMessage[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.sessionStorage.getItem(MSGS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as GemmaMessage[]
    // Drop any placeholder left mid-flight when the page unloaded.
    return Array.isArray(parsed) ? parsed.filter((m) => !m.thinking) : []
  } catch {
    return []
  }
}

function saveMessages(msgs: GemmaMessage[]): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      MSGS_KEY,
      JSON.stringify(msgs.filter((m) => !m.thinking).slice(-40)),
    )
  } catch { /* quota — best effort */ }
}

export function Gemma() {
  const location = useLocation()
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()
  const { signedIn } = useApp()

  const [role, setRole] = useState<Role>(null)
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<GemmaMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [openStone, setOpenStone] = useState<Diamond | null>(null)

  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesRef = useRef<GemmaMessage[]>(messages)
  messagesRef.current = messages

  const hidden = isHiddenPath(location.pathname)
  const hasMessages = messages.length > 0

  // ── Restore open/closed + thread once on mount ──────────────────
  useEffect(() => {
    setMessages(loadMessages())
    try { setOpen(window.sessionStorage.getItem(OPEN_KEY) === '1') } catch { /* ignore */ }
  }, [])

  // ── Resolve role from the verified token (mirrors Chat.tsx) ──────
  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState(async (user) => {
        if (!user) { setRole(null); return }
        try {
          const tok = await user.getIdTokenResult()
          const r = (tok.claims as { role?: string }).role
          setRole(r === 'admin' || r === 'supplier' ? r : 'buyer')
        } catch { setRole('buyer') }
      })
    } catch { /* no auth — anon buyer persona */ }
    return () => unsub?.()
  }, [])

  // ── Persist thread + open state ─────────────────────────────────
  useEffect(() => { saveMessages(messages) }, [messages])
  useEffect(() => {
    try { window.sessionStorage.setItem(OPEN_KEY, open ? '1' : '0') } catch { /* ignore */ }
  }, [open])

  // Keep the transcript pinned to the newest message.
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages.length, open])

  // Focus the composer when the panel opens.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 220)
      return () => clearTimeout(t)
    }
  }, [open])

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const greeting = useMemo(() => greetingFor(role), [role])
  const suggestions = useMemo(() => suggestionsFor(role), [role])

  function patchMessage(id: string, fn: (m: GemmaMessage) => GemmaMessage) {
    setMessages((prev) => prev.map((m) => (m.id === id ? fn(m) : m)))
  }

  async function sendMessage(text: string) {
    const trimmed = text.trim()
    if (!trimmed || sending) return
    setInput('')

    const userMsg: GemmaMessage = { id: crypto.randomUUID(), role: 'user', text: trimmed, ts: Date.now() }
    const placeholderId = crypto.randomUUID()
    const placeholder: GemmaMessage = {
      id: placeholderId, role: 'assistant', text: pickPlaceholder(trimmed), thinking: true, ts: Date.now() + 1,
    }

    // History = the turns BEFORE this one.
    const history: ChatMessageHistory[] = messagesRef.current
      .slice(-6)
      .filter((m) => m.role !== 'system' && !m.thinking)
      .map((m) => ({ role: m.role as 'user' | 'assistant', text: m.text }))

    setMessages((prev) => [...prev, userMsg, placeholder])
    setSending(true)

    // Admin-only live context (platform KPIs). Buyer order status + supplier
    // feed checks are server-side TOOLS now — the agent fetches them itself.
    const roleContext = signedIn && role === 'admin' ? await buildAdminContext(trimmed) : undefined

    // The most recent search's filters — lets "only VS1" refine server-side.
    const lastFilters = [...messagesRef.current].reverse()
      .find((m) => m.role === 'assistant' && m.appliedFilters)?.appliedFilters

    try {
      const r = await chatApi.send({
        userText: trimmed,
        conversationHistory: history,
        lastFilters,
        isAuthenticated: signedIn,
        role: role ?? undefined,
        systemContext: roleContext,
      })
      patchMessage(placeholderId, (m) => ({
        ...m, text: r.response, thinking: false, steps: r.steps, results: r.results,
        appliedFilters: r.appliedFilters, actions: r.actions, confirm: r.confirm,
      }))
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `Couldn't reach the assistant (${err.status}). Try again in a moment.`
          : "Couldn't reach the assistant. Try again in a moment."
      patchMessage(placeholderId, (m) => ({ ...m, text: msg, thinking: false }))
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  function pickPlaceholder(userText: string): string {
    const t = userText.toLowerCase()
    if (/order|invoice|payment/.test(t)) return 'Looking at your orders…'
    if (/cart|shortlist/.test(t)) return 'Pulling up your cart…'
    if (/unavailable|out of stock|sold/.test(t)) return 'Checking your stock…'
    if (/price|repric/.test(t)) return 'Looking up that stone…'
    if (/round|oval|pear|emerald|cushion|princess|marquise|heart|asscher|radiant/.test(t)) {
      return 'Searching the inventory…'
    }
    if (/biggest|largest|cheapest|most expensive/.test(t)) return 'Scanning current stock…'
    return 'Thinking…'
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendMessage(input)
    }
  }

  /**
   * Supplier/admin acts on an inventory-update confirm card. Mirrors
   * Chat.tsx#onConfirmSupplierAction — the server re-checks the token role +
   * stone ownership, so a leaked confirm card can never let a buyer write.
   */
  async function onConfirm(msgId: string, confirm: ChatConfirm, choice: 'yes' | 'skip') {
    if (choice === 'skip') {
      patchMessage(msgId, (m) => ({ ...m, confirmResolved: 'skipped' }))
      return
    }
    let outcome = ''
    try {
      if (confirm.payload.kind === 'mark_unavailable') {
        const r = await sellerApi.markUnavailable(confirm.payload.stockIds)
        outcome = r.flagged.length
          ? `Marked ${r.flagged.join(', ')} as unavailable.`
          : `Couldn't mark those${r.skipped[0] ? ` (${r.skipped[0].reason})` : ''}.`
      } else if (confirm.payload.kind === 'update_contact') {
        await sellerApi.updateContact(confirm.payload.updates)
        outcome = 'Contact updated — verified buyers now see the new details on your stones.'
      } else {
        const u = confirm.payload.updates
        const stone: { stockId: string; rate?: number; cut?: string } = { stockId: confirm.payload.stockId }
        const unsupported: string[] = []
        if (typeof u.price === 'number') stone.rate = u.price
        if (u.cut) stone.cut = u.cut
        if (u.color) unsupported.push('color')
        if (u.clarity) unsupported.push('clarity')
        if (u.carat) unsupported.push('carat')
        if (stone.rate == null && stone.cut == null) {
          outcome = unsupported.length
            ? `${unsupported.join('/')} updates aren't supported from chat yet — edit that stone in My stock.`
            : 'Nothing to update.'
        } else {
          const r = await sellerApi.updateStock([stone])
          outcome = r.updated.length
            ? `Updated ${r.updated.join(', ')}.${unsupported.length ? ` (${unsupported.join('/')} not supported here — use My stock.)` : ''}`
            : `Couldn't update${r.skipped[0] ? ` (${r.skipped[0].reason})` : ''}.`
        }
      }
    } catch {
      outcome = "That update didn't go through — try again from My stock."
    }
    setMessages((prev) => [
      ...prev.map((m) => (m.id === msgId ? { ...m, confirmResolved: 'done' as const } : m)),
      { id: crypto.randomUUID(), role: 'system' as const, text: outcome, ts: Date.now() },
    ])
  }

  function onAction(a: ChatAction) {
    navigate(a.route)
    setOpen(false)
  }

  if (hidden) return null

  const fabTransition = reduceMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 380, damping: 26 }

  return (
    <>
      {/* ── Resting FAB ─────────────────────────────────────────── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            type="button"
            onClick={() => setOpen(true)}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.6, y: 20 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.6, y: 20 }}
            transition={fabTransition}
            whileHover={reduceMotion ? undefined : { scale: 1.04 }}
            whileTap={reduceMotion ? undefined : { scale: 0.96 }}
            aria-label="Ask Gemma — AI assistant"
            title="Ask Gemma"
            data-testid="gemma-fab"
            /* Compact icon-only FAB (Tej 2026-07-27) — the old wide pill sat
               over the bottom-right and covered the pager / last table row's
               actions. Discoverability now lives in the "Ask Gemma" nav tab;
               this stays as a small quick launcher with a tiny corner footprint. */
            className="fixed bottom-5 right-5 z-[80] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#0a1f3c]/70 backdrop-blur-xl text-gold-soft shadow-[0_10px_34px_-8px_rgba(6,17,31,0.6)] ring-1 ring-gold/30 hover:ring-gold/55 hover:bg-[#0a1f3c]/85 transition-all"
          >
            <GemIcon className="h-6 w-6 shrink-0 text-gold-soft" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Assistant panel ─────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Scrim — mobile only (desktop panel floats over the corner). */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.2 }}
              onClick={() => setOpen(false)}
              className="sm:hidden fixed inset-0 z-[80] bg-navy/40 backdrop-blur-sm"
              aria-hidden
            />
            <motion.div
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.98 }}
              animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.98 }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 32 }}
              role="dialog"
              aria-label="Gemma AI assistant"
              data-testid="gemma-panel"
              style={{ background: 'linear-gradient(158deg, rgba(16,35,62,0.82) 0%, rgba(9,21,39,0.80) 100%)' }}
              className="fixed z-[81] flex flex-col overflow-hidden backdrop-blur-2xl backdrop-saturate-150 shadow-[0_30px_90px_-16px_rgba(6,17,31,0.78)] ring-1 ring-white/10
                         before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-gold/60 before:to-transparent before:content-['']
                         inset-x-0 bottom-0 top-[8vh] rounded-t-[1.5rem]
                         sm:inset-x-auto sm:bottom-5 sm:right-5 sm:top-auto
                         sm:h-[min(640px,calc(100dvh-3rem))] sm:w-[400px] sm:rounded-[1.5rem] sm:border sm:border-gold/20"
            >
              {/* Header */}
              <div className="flex items-center gap-2.5 border-b border-white/10 px-4 py-3 text-white">
                <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-paper/10 ring-1 ring-white/15">
                  <GemIcon className="h-5 w-5 text-gold-soft" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-[17px] font-semibold leading-none">Gemma</p>
                  <p className="lqd-mono mt-1 text-[10px] uppercase tracking-[0.14em] text-white/55">
                    {role === 'supplier' ? 'Stock assistant'
                      : role === 'admin' ? 'Platform copilot'
                        : 'DiaEx AI · beta'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close Gemma"
                  data-testid="gemma-close"
                  className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/70 hover:bg-paper/10 hover:text-white transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Transcript */}
              <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="gemma-transcript">
                {/* Greeting — always shown at the top of the thread. */}
                <div className="flex gap-2.5" data-testid="gemma-greeting">
                  <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-gold/25">
                    <GemIcon className="h-4 w-4 text-gold-soft" />
                  </span>
                  <p className="lqd-bubble max-w-[85%] rounded-2xl rounded-tl-md px-3.5 py-2.5 text-[14px] leading-[1.55] text-ink">
                    {greeting}
                  </p>
                </div>

                {!hasMessages && (
                  <div className="flex flex-wrap gap-1.5 pl-9" data-testid="gemma-suggestions">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => void sendMessage(s)}
                        className="rounded-lg border border-white/12 bg-white/[0.04] px-3 py-1.5 text-[12.5px] font-medium tracking-tight text-white/90 backdrop-blur transition-colors hover:border-gold/55 hover:bg-white/[0.08]"
                        data-testid="gemma-suggestion"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                <AnimatePresence initial={false}>
                  {messages.map((m) => (
                    <Bubble
                      key={m.id}
                      msg={m}
                      blurPrice={!signedIn}
                      onOpenStone={setOpenStone}
                      onAction={onAction}
                      onConfirm={onConfirm}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {/* Composer */}
              <div className="border-t border-white/10 p-2.5">
                <div className="rounded-2xl border border-white/[0.12] bg-white/[0.07] p-1.5 transition-colors duration-200 focus-within:border-white/25">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                      rows={1}
                      placeholder="Ask Gemma anything…"
                      className="flex-1 resize-none bg-transparent px-2.5 py-2 text-[14.5px] text-white placeholder:text-white/40 focus:outline-none max-h-28"
                      data-testid="gemma-input"
                    />
                    {/* Refined icon send — hairline disc, muted when empty, gold
                        when enabled/hover. Matches /chat's .lqd-iconsend register,
                        hand-tuned for the always-dark panel. */}
                    <button
                      type="button"
                      onClick={() => void sendMessage(input)}
                      disabled={!input.trim() || sending}
                      aria-label="Send"
                      data-testid="gemma-send"
                      className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl border transition-all active:scale-95 ${
                        !input.trim() || sending
                          ? 'border-white/12 bg-white/[0.04] text-white/35'
                          : 'border-gold/45 bg-gold/12 text-gold-soft hover:border-gold/70 hover:bg-gold/20'
                      }`}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Thin toolbar — mono keyboard hints (keyboard-first tell). */}
                  <div className="mt-1.5 flex items-center gap-2.5 border-t border-white/[0.08] px-1 pt-1.5">
                    <span className="inline-flex items-center gap-1">
                      <span className="lqd-kbd !text-white/55 !bg-white/[0.06] !border-white/12">⏎</span>
                      <span className="lqd-mono text-[10.5px] text-white/45">send</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="lqd-kbd !text-white/55 !bg-white/[0.06] !border-white/12">⇧⏎</span>
                      <span className="lqd-mono text-[10.5px] text-white/45">newline</span>
                    </span>
                  </div>
                </div>
                <p className="lqd-mono mt-1.5 text-center text-[10px] tracking-wide text-white/40">
                  Gemma · beta{!signedIn ? ' · sign in for prices + help' : ''}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <MediaModal
        open={!!openStone}
        onClose={() => setOpenStone(null)}
        stone={openStone}
        blurPrice={!signedIn}
      />
    </>
  )
}

// ── The Gemma gem mark — a faceted brilliant, champagne-gold on navy. ────
function GemIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" className={className} aria-hidden>
      <polygon points="12 2 19 9 12 22 5 9" fill="currentColor" fillOpacity="0.18" />
      <line x1="5" y1="9" x2="19" y2="9" />
      <line x1="9" y1="9" x2="12" y2="22" />
      <line x1="15" y1="9" x2="12" y2="22" />
    </svg>
  )
}

// ── Message bubble — assistant text, result cards, actions, confirm. ─────
// Self-contained (not shared with Chat.tsx) so the /chat page can't regress
// from Gemma changes, at the cost of a small, deliberate duplication.
function Bubble({
  msg, blurPrice, onOpenStone, onAction, onConfirm,
}: {
  msg: GemmaMessage
  blurPrice: boolean
  onOpenStone: (s: Diamond) => void
  onAction: (a: ChatAction) => void
  onConfirm: (msgId: string, confirm: ChatConfirm, choice: 'yes' | 'skip') => void
}) {
  if (msg.role === 'system') {
    return <p className="text-center text-[12px] text-soft italic">{msg.text}</p>
  }
  if (msg.role === 'user') {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-end"
        data-testid="gemma-msg-user"
      >
        <p className="lqd-user max-w-[85%] rounded-2xl rounded-br-md px-3.5 py-2 text-[14px] text-white">
          {msg.text}
        </p>
      </motion.div>
    )
  }
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-2.5"
      data-testid="gemma-msg-assistant"
    >
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-gold/25">
        <GemIcon className="h-4 w-4 text-gold-soft" />
      </span>
      <div className="min-w-0 flex-1 flex flex-col gap-2.5">
        {/* Light agentic trail — what Gemma actually did, with counts.
            Mono metadata layer (the "technical" tell); white on dark panel. */}
        {!msg.thinking && msg.steps && msg.steps.length > 0 && (
          <div className="flex flex-col gap-0.5" data-testid="gemma-steps">
            {msg.steps.map((s, i) => (
              <p key={i} className="lqd-mono flex items-center gap-1.5 text-[10.5px] text-white/45 leading-snug tracking-tight">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gold">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="truncate">{s.label}{s.detail ? <span className="text-white/35"> — {s.detail}</span> : null}</span>
              </p>
            ))}
          </div>
        )}
        {/* Thinking state carries the blinking caret (the only live state —
            text is replaced wholesale when the turn resolves). */}
        <p className={
          'max-w-[92%] text-[14px] leading-[1.55] whitespace-pre-wrap ' +
          (msg.thinking
            ? 'px-1 py-1 thinking-shimmer lqd-caret'
            : 'lqd-bubble rounded-2xl rounded-tl-md px-3.5 py-2.5 text-ink')
        }>
          {/* The backend appends a " [Yes] [Skip]" text fallback to confirm
              prompts; we render real buttons below, so strip the literal. */}
          {msg.confirm ? msg.text.replace(/\s*\[Yes\]\s*\[Skip\]\s*$/i, '') : msg.text}
        </p>

        {msg.confirm && !msg.confirmResolved && (
          <div className="flex flex-wrap gap-2" data-testid="gemma-confirm">
            <button
              type="button"
              onClick={() => onConfirm(msg.id, msg.confirm!, 'yes')}
              className="inline-flex items-center gap-1 rounded-full bg-gold px-4 py-1.5 text-[13px] font-semibold text-navy hover:brightness-110 transition-all"
              data-testid="gemma-confirm-yes"
            >
              Yes, do it
            </button>
            <button
              type="button"
              onClick={() => onConfirm(msg.id, msg.confirm!, 'skip')}
              className="inline-flex items-center rounded-full border border-white/20 px-4 py-1.5 text-[13px] font-medium text-white/90 hover:bg-white/10 transition-colors"
              data-testid="gemma-confirm-skip"
            >
              Skip
            </button>
          </div>
        )}
        {msg.confirmResolved === 'skipped' && (
          <p className="text-[12px] text-soft italic">Skipped.</p>
        )}

        {msg.results && msg.results.length > 0 && (
          <div className="space-y-1.5" data-testid="gemma-results">
            {msg.results.slice(0, 8).map((s) => (
              <DiamondRow
                key={stoneKey(s)}
                stone={s}
                onOpen={onOpenStone}
                blurPrice={blurPrice}
              />
            ))}
          </div>
        )}

        {msg.actions && msg.actions.length > 0 && !msg.thinking && (
          <div className="flex flex-wrap gap-1.5">
            {msg.actions.map((a, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onAction(a)}
                className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[12px] font-medium text-white/90 hover:bg-white/10 transition-colors"
                data-testid="gemma-action"
              >
                {a.label}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  )
}
